import { useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Chip,
  Button,
  Stack,
  Divider,
  Alert,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import AddIcon from '@mui/icons-material/Add'
import { FieldEditor } from './FieldEditor.js'
import { ConfirmDeleteButton } from './ConfirmDeleteButton.js'
import type { CollectionAnnotation, FieldAnnotation } from './types.js'

type Props = {
  lid: string
  initial: CollectionAnnotation
  onSave: (annotation: CollectionAnnotation) => Promise<void>
  /** Archive (delete) this collection annotation; absent for unsaved collections. */
  onArchive?: () => Promise<void>
}

export const CollectionForm = ({ lid, initial, onSave, onArchive }: Props) => {
  const [description, setDescription] = useState(initial.description ?? '')
  const [readOnly, setReadOnly] = useState(initial.readOnly ?? false)
  const [tags, setTags] = useState<string[]>(initial.tags ?? [])
  const [fields, setFields] = useState<Record<string, FieldAnnotation>>(
    initial.fields ?? {},
  )
  const [tagInput, setTagInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    await onSave({ description, readOnly, tags, fields })
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 2500)
  }

  const addTag = () => {
    const tag = tagInput.trim()
    if (tag && !tags.includes(tag)) setTags([...tags, tag])
    setTagInput('')
  }

  const addField = () => {
    const name = prompt('Feldname:')
    if (name?.trim())
      setFields({ ...fields, [name.trim()]: { description: '' } })
  }

  return (
    <Box p={4} maxWidth={760}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
      >
        <Typography variant="h5" fontWeight={700}>
          {lid}
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            color={saved ? 'success' : 'primary'}
          >
            {saving ? 'Speichern…' : saved ? 'Gespeichert' : 'Speichern'}
          </Button>
          {onArchive && (
            <ConfirmDeleteButton
              onConfirm={onArchive}
              itemLabel={lid}
              disabled={saving}
            />
          )}
        </Box>
      </Box>

      {saved && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Annotation gespeichert.
        </Alert>
      )}

      <Stack spacing={3}>
        <TextField
          label="Beschreibung"
          multiline
          rows={4}
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Was enthält diese Collection? Welche Business-Logik steckt dahinter?"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={readOnly}
              onChange={(e) => setReadOnly(e.target.checked)}
            />
          }
          label="Read-only (verhindert Schreibzugriff durch MCP Tools)"
        />

        <Box>
          <Typography variant="subtitle2" fontWeight={600} mb={1}>
            Tags
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mb={1.5}>
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                onDelete={() => setTags(tags.filter((t) => t !== tag))}
              />
            ))}
          </Box>
          <Box display="flex" gap={1}>
            <TextField
              size="small"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addTag()}
              placeholder="Tag hinzufügen…"
            />
            <Button variant="outlined" size="small" onClick={addTag}>
              +
            </Button>
          </Box>
        </Box>

        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              Felder
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addField}>
              Feld
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {Object.keys(fields).length === 0 ? (
            <Typography variant="body2" color="text.disabled">
              Noch keine Felder dokumentiert.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {Object.entries(fields).map(([name, annotation]) => (
                <FieldEditor
                  key={name}
                  name={name}
                  annotation={annotation}
                  onChange={(updated) =>
                    setFields({ ...fields, [name]: updated })
                  }
                  onRemove={() => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { [name]: _, ...rest } = fields
                    setFields(rest)
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  )
}
